import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Checkbox, FormInstance, Select, Table } from 'antd';
import Upload, { RcFile } from 'antd/es/upload';
import { LANGUAGE_LIST, SUBTITLE_FILE_EXTENSION } from 'constants/index';
import { contentService } from 'content/service/contentService';
import { SubtitleInterface } from 'content/type';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import subtitleHandlers from '../../handlers/SubtitleHandlers';

export const Subtitle: React.FC<{ form: FormInstance }> = ({ form }) => {
  const { id } = useParams<{ id: string }>();
  const [dataSource, setDataSource] = useState<SubtitleInterface[]>([]);
  const {
    handleLanguageChange,
    handleFileChange,
    handleAddSubtitle,
    handleSubtitleDownload,
    handleIntegratedChange,
    handleDeleteSubtitle,
  } = subtitleHandlers({
    form,
    contentId: Number(id),
    setDataSource,
  });

  const columns = [
    {
      title: 'Lang',
      dataIndex: 'lang',
      key: 'lang',
      width: '10%',
      render: (_text: string, record: SubtitleInterface) => (
        <Select
          options={LANGUAGE_LIST.map(language => ({ label: language, value: language }))}
          style={{ width: '100%', marginBottom: '10px' }}
          onChange={value => handleLanguageChange(value, { id: record.id.toString() })}
          value={record.lang}
        />
      ),
    },
    {
      title: 'File',
      dataIndex: 'file',
      key: 'file',
      render: (_file: RcFile | undefined, record: SubtitleInterface): React.ReactNode => {
        const isUploadedFile = typeof record.file === 'string' || record.external_url;
        const fileName = isUploadedFile
          ? ((record.file || record.external_url) as unknown as string).split('/').pop()
          : (record.file as RcFile)?.name;

        return isUploadedFile ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                flex: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {fileName}
            </span>
            <Button
              type="link"
              onClick={() => handleSubtitleDownload(record)}
              style={{ padding: 0 }}
            >
              Download
            </Button>
          </div>
        ) : (
          <Upload
            key={record.id.toString()}
            accept={SUBTITLE_FILE_EXTENSION.join(',')}
            beforeUpload={() => false} // 자동 업로드 방지
            onChange={info => handleFileChange(info.file as RcFile, { id: record.id.toString() })}
            listType="text"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />} style={{ width: '100%' }}>
              Subtitle file upload
            </Button>
          </Upload>
        );
      },
    },
    {
      title: 'Embedded',
      dataIndex: 'is_integrated',
      key: 'is_integrated',
      render: (_text: boolean, record: SubtitleInterface) => (
        <Checkbox checked={record.is_integrated} onChange={() => handleIntegratedChange(record)} />
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (_text: string, record: SubtitleInterface) => (
        <Button type="link" onClick={() => handleDeleteSubtitle(record)}>
          Delete
        </Button>
      ),
    },
  ];

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const subtitleList = await contentService.fetchSubtitleList(Number(id));
      setDataSource(subtitleList);
    };
    fetchData();
  }, [id]);

  return (
    <>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        rowKey={record => record.id.toString()}
      />
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        style={{ width: '100%', marginTop: '10px' }}
        onClick={() => handleAddSubtitle(dataSource.length + 1)}
      >
        New Subtitle
      </Button>
    </>
  );
};

export default Subtitle;
